import type { ImageType } from '@vizit/api';
import { formatTagIdsParam } from '@vizit/organisms';
import * as React from 'react';
import { AssetTableFilterOptions } from '../../components';
import { AssetTableContext } from './asset-table-context';
import {
  AssetTableActionType,
  type AssetTableState,
  DEFAULT_FILTER_STATE,
  assetTableFilterReducer,
} from './asset-table-provider.state';

interface AssetTableProviderProps extends React.PropsWithChildren, AssetTableState {
  /**
   * The ID of the portfolio to fetch assets for
   *
   * @optional
   * @type {string}
   */
  workspaceId?: string;
  /**
   * The function to call when the asset table filters change
   *
   * @optional
   * @type {(filter: AssetTableFilterOptions, value: string) => void}
   * @param filter - The filter option to change
   * @param value - The new value for the filter
   */
  onAssetTableFilterChange?: (filter: AssetTableFilterOptions, value: string) => void;
}

/**
 * Provider component for the asset table context.
 * Manages the state for asset table filters, sorting, and pagination.
 *
 * @param props - The provider props
 */
function AssetTableProvider(props: AssetTableProviderProps) {
  const {
    children,
    workspaceId,
    limit,
    sortBy,
    sortDirection,
    productCategoryId,
    productCategoryName,
    imageType,
    tagIds,
    onAssetTableFilterChange,
  } = props;

  const [filterState, dispatch] = React.useReducer(assetTableFilterReducer, {
    limit: limit ?? DEFAULT_FILTER_STATE.limit,
    sortBy: sortBy ?? DEFAULT_FILTER_STATE.sortBy,
    sortDirection: sortDirection ?? DEFAULT_FILTER_STATE.sortDirection,
    productCategoryId: productCategoryId ?? DEFAULT_FILTER_STATE.productCategoryId,
    productCategoryName: productCategoryName ?? DEFAULT_FILTER_STATE.productCategoryName,
    imageType: imageType ?? DEFAULT_FILTER_STATE.imageType,
    tagIds: tagIds ?? DEFAULT_FILTER_STATE.tagIds,
  });

  const tagIdsParam = formatTagIdsParam(tagIds);
  const [syncedTagIdsParam, setSyncedTagIdsParam] = React.useState(tagIdsParam);
  if (tagIdsParam !== syncedTagIdsParam) {
    setSyncedTagIdsParam(tagIdsParam);
    dispatch({ type: AssetTableActionType.SET_TAG_IDS, payload: { tagIds: tagIds ?? [] } });
  }

  const handleOnFilterProductCategory = React.useCallback(
    (productCategoryId: string, productCategoryName: string) => {
      dispatch({
        type: AssetTableActionType.SET_PRODUCT_CATEGORY_ID,
        payload: { productCategoryId, productCategoryName },
      });

      onAssetTableFilterChange?.(AssetTableFilterOptions.ProductCategory, productCategoryId);
      onAssetTableFilterChange?.(AssetTableFilterOptions.ProductCategoryName, productCategoryName);
    },
    [onAssetTableFilterChange],
  );

  const handleOnFilterImageType = React.useCallback(
    (imageType: ImageType | null) => {
      dispatch({
        type: AssetTableActionType.SET_IMAGE_TYPE,
        payload: { imageType },
      });

      onAssetTableFilterChange?.(AssetTableFilterOptions.ImageType, imageType || '');
    },
    [onAssetTableFilterChange],
  );

  const handleOnFilterTags = React.useCallback(
    (tagIds: string[]) => {
      dispatch({ type: AssetTableActionType.SET_TAG_IDS, payload: { tagIds } });

      onAssetTableFilterChange?.(AssetTableFilterOptions.Tags, formatTagIdsParam(tagIds) ?? '');
    },
    [onAssetTableFilterChange],
  );

  return (
    <AssetTableContext.Provider
      value={{
        ...filterState,
        workspaceId,
        onFilterProductCategory: handleOnFilterProductCategory,
        onFilterImageType: handleOnFilterImageType,
        onFilterTags: handleOnFilterTags,
      }}
    >
      {children}
    </AssetTableContext.Provider>
  );
}

/**
 * Hook to access the asset table context.
 * Must be used within an AssetTableProvider.
 *
 * @throws Error if used outside of AssetTableProvider
 * @returns The asset table context
 */
const useAssetTable = () => {
  const context = React.useContext(AssetTableContext);

  if (!context) {
    throw new Error('useAssetTable must be used within an AssetTableProvider');
  }

  return context;
};

export { AssetTableProvider, useAssetTable };
